import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

export interface VirtualShopNotification {
  id: number;
  shopOwnerId: string;
  /** null for notices not tied to an order (e.g. the condition campaign). */
  orderId: number | null;
  type: string;
  title: string;
  message: string;
  isRead: boolean;
  createdAt: string;
}

export function useVirtualShopNotifications(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ["/api/virtual-shop/notifications"],
    queryFn: async () => {
      const res = await fetch("/api/virtual-shop/notifications");
      if (!res.ok) throw new Error("Failed to fetch notifications");
      return res.json() as Promise<VirtualShopNotification[]>;
    },
    enabled: options?.enabled ?? true,
    refetchInterval: 30000,
  });
}

export function useVirtualShopUnreadCount(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ["/api/virtual-shop/notifications/unread-count"],
    queryFn: async () => {
      const res = await fetch("/api/virtual-shop/notifications/unread-count");
      if (!res.ok) throw new Error("Failed to fetch unread count");
      return res.json() as Promise<{ count: number }>;
    },
    enabled: options?.enabled ?? true,
    refetchInterval: 15000,
  });
}

export function useMarkVirtualShopNotificationRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (notificationId: number) => {
      const res = await fetch(`/api/virtual-shop/notifications/${notificationId}/read`, {
        method: "PATCH",
      });
      if (!res.ok) throw new Error("Failed to mark as read");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/virtual-shop/notifications"] });
      queryClient.invalidateQueries({ queryKey: ["/api/virtual-shop/notifications/unread-count"] });
    },
  });
}

export function useMarkAllVirtualShopNotificationsRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const res = await fetch("/api/virtual-shop/notifications/mark-all-read", {
        method: "PATCH",
      });
      if (!res.ok) throw new Error("Failed to mark all as read");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/virtual-shop/notifications"] });
      queryClient.invalidateQueries({ queryKey: ["/api/virtual-shop/notifications/unread-count"] });
    },
  });
}
